import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Tooltip
} from "@mui/material";
import {
  Assignment as PlanIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  LocalFireDepartment as WarmupIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarMonth as CalendarIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  TrendingUp as LevelIcon,
  Build as EquipmentIcon,
  Accessibility as BodyPartIcon,
  EmojiEvents as GoalsIcon
} from "@mui/icons-material";
import {Fragment, useEffect, useState} from "react";
import {changeProgram, getHistory, getPlan} from "../../services/PlanSerice";
import moment from "moment";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {useMessageContext} from "../../contexts/MessageContext";
import ChangeProgramDialog from "../ChnageProgramDialog";
import ProgramSettingsDialog from "./ProgramSettingsDialog";
import ProgramWarmupDialog from "./ProgramWarmupDialog";
import {bodyParts} from "../../enums/BodyParts";
import {SecondaryMuscles} from "../../enums/SecondaryMuscles";
import {useNavigate} from "react-router-dom";
import {experienceLevels} from "../../enums/ExperienceLevels";
import {EquipmentTypes} from "../../enums/EquipmentType";
import {colors, borders, shadows} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {StatusChip} from "../ui";
import {RTL} from "../../cache/CacheRtl";

const INITIAL_DISPLAY_COUNT = 5;

const tdBase = {borderBottom: borders.default, py: 0, px: 2, height: "56px", fontSize: "13px", color: colors.textPrimary};
const thBase = {...tdBase, height: "42px", color: colors.textMuted, fontSize: "11px", fontWeight: 600, backgroundColor: colors.bg, letterSpacing: "0.3px"};

const tdCoach = {borderBottom: `1px solid ${coachT.borderLight}`, py: 0, px: 2, height: "56px", fontSize: "13px", color: coachT.text};
const thCoach = {...tdCoach, height: "42px", color: coachT.t3, fontSize: "10.5px", fontWeight: 700, backgroundColor: coachT.bg, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${coachT.border}`};

const ClientPlansSection = ({client, handleReload, coach = false}) => {
  const th = coach ? thCoach : thBase;
  const td = coach ? tdCoach : tdBase;
  const [planOpen, setPlanOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showAll, setShowAll] = useState(false);

  const {notificationHandler} = useMessageContext();

  const [historyPlans, setHistoryPlans] = useState();
  const [refetchHistory, setRefetchHistory] = useState(false);
  const [changeProgramState, setChangeProgramState] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  // The warmup dialog needs the FULL program (MobileProgram): getHistory returns MiniProgram, which
  // deliberately omits the two 4000-char warmup columns rather than carrying them on every row of a
  // potentially long list. So fetch the one program the coach clicked, and only open once it lands —
  // opening first and populating after would read as data loss.
  const [warmupProgram, setWarmupProgram] = useState(null);

  let navigate = useNavigate();

  const handleChangeProgram = (templateId, includeRmWeek = false, advancedSettings = null) => {
    changeProgram(client.id, templateId, includeRmWeek, advancedSettings)
        .then(notificationHandler.success("תוכנית שונתה בהצלחה"))
        .then(() => setChangeProgramState(false))
        .then(() => setRefetchHistory(prv => !prv))
        .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
  }

  useEffect(() => {
    getHistory(client.id)
        .then(res => setHistoryPlans(res.data))
        .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
  }, [refetchHistory])

  /**
   * Open the warmup editor for the trainee's ACTIVE program (same target as the settings button
   * beside it). Fetches the full MobileProgram first because the history list only carries
   * MiniProgram, which has no warmup fields — see the warmupProgram state comment.
   */
  const handleOpenWarmupDialog = () => {
    const active = historyPlans?.find(p => p.active);
    if (!active) {
      notificationHandler.error("אין תוכנית פעילה");
      return;
    }
    getPlan(client.id, active.id)
        .then(res => setWarmupProgram(res.data))
        .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };


  const toggleRowExpansion = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const renderDetailedInfo = (plan) => (
      <Box sx={{ padding: "20px", backgroundColor: colors.bg, borderRadius: borders.radius.sm, margin: "8px" }}>
        {plan.descriptionHeb && (
            <Typography sx={{
              fontSize: "14px",
              color: colors.textSecondary,
              marginBottom: "20px",
              fontStyle: "italic",
              lineHeight: "20px"
            }}>
              {plan.descriptionHeb}
            </Typography>
        )}

        <Box sx={{
          display: "grid",
          gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)"},
          gap: "16px 24px",
          rowGap: "12px"
        }}>
          <DetailRow
              icon={<FitnessCenterIcon />}
              label="אימונים בשבוע"
              value={`${plan.numOfWorkouts}`}
          />
          {plan.numOfWeeks && (
              <DetailRow icon={<CalendarIcon />} label="מספר שבועות" value={`${plan.numOfWeeks}`} />
          )}
          {(plan.workoutMinDuration || plan.workoutMaxDuration) && (
              <DetailRow
                  icon={<TimerIcon />}
                  label="משך אימון"
                  value={plan.workoutMinDuration && plan.workoutMaxDuration
                      ? `${plan.workoutMinDuration}-${plan.workoutMaxDuration} דק'`
                      : plan.workoutMinDuration
                          ? `${plan.workoutMinDuration}+ דק'`
                          : `עד ${plan.workoutMaxDuration} דק'`}
              />
          )}
          <DetailRow
              icon={<PersonIcon />}
              label="מגדר"
              value={plan.gender === "FEMALE" ? "נקבה" : plan.gender === "MALE" ? "זכר" : "אין מידע"}
          />
          {plan.experienceLevel && (
              <DetailRow
                  icon={<LevelIcon />}
                  label="רמת מתאמן"
                  value={experienceLevels.find(lvl => lvl.value === plan.experienceLevel)?.name || plan.experienceLevel}
              />
          )}
          {plan.equipmentType && (
              <DetailRow
                  icon={<EquipmentIcon />}
                  label="ציוד"
                  value={EquipmentTypes.find(eq => eq.value === plan.equipmentType)?.name || plan.equipmentType}
              />
          )}
          {plan.bodyPart && (
              <DetailRow
                  icon={<BodyPartIcon />}
                  label="מיקוד עיקרי"
                  value={bodyParts.find(bp => bp.value === plan.bodyPart)?.name || "אין מידע"}
              />
          )}
          {plan.secondaryMuscle && (
              <DetailRow
                  icon={<BodyPartIcon />}
                  label="מיקוד משני"
                  value={SecondaryMuscles.find(sm => sm.value === plan.secondaryMuscle)?.name}
              />
          )}
          {plan.goals && plan.goals.length > 0 && (
              <Box sx={{gridColumn: {xs: "1 / -1", md: "span 2"}}}>
                <DetailRow
                    icon={<GoalsIcon />}
                    label="מטרות"
                    value={
                      <Box sx={{display: "flex", flexWrap: "wrap", gap: "6px"}}>
                        {Array.from(plan.goals).map((goal, index) => (
                            <Chip
                                key={index}
                                label={goal}
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  backgroundColor: colors.infoBg,
                                  color: colors.infoText,
                                  fontWeight: 500
                                }}
                            />
                        ))}
                      </Box>
                    }
                />
              </Box>
          )}
        </Box>
      </Box>
  );

  return (
      <RTL>
      <Paper
          dir="rtl"
          sx={{
            backgroundColor: coach ? coachT.white : colors.white,
            border: coach ? `1px solid ${coachT.border}` : borders.default,
            borderRadius: coach ? coachT.r : borders.radius.lg,
            boxShadow: coach ? coachT.sh : shadows.card,
            overflow: "hidden",
          }}
          elevation={0}
      >
        <ChangeProgramDialog
            trainee={client}
            handleChangeProgram={handleChangeProgram}
            isOpen={changeProgramState}
            close={() => setChangeProgramState(false)}
        />
        <ProgramSettingsDialog
            client={client}
            program={historyPlans?.find(p => p.active)}
            handleReload={() => setRefetchHistory(prv => !prv)}
            isOpen={settingsDialogOpen}
            close={() => setSettingsDialogOpen(false)}
        />

        {warmupProgram && (
            <ProgramWarmupDialog
                program={warmupProgram}
                handleReload={() => setRefetchHistory(prv => !prv)}
                isOpen={!!warmupProgram}
                close={() => setWarmupProgram(null)}
            />
        )}

        {/* Header */}
        <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: coach ? "16px 22px" : "16px 20px",
              backgroundColor: coach ? coachT.bg : colors.bg,
              borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
            }}
        >
          <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
            {coach ? (
                <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                  <PlanIcon sx={{color: coachT.t2, fontSize: 22}}/>
                </Box>
            ) : (
                <PlanIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
            )}
            <Box>
              <Typography sx={{fontWeight: coach ? 800 : 600, fontSize: coach ? "17px" : "16px", color: coach ? coachT.text : colors.textPrimary, letterSpacing: coach ? "-0.2px" : 0}}>
                היסטורית אימונים
              </Typography>
              {coach && <Typography sx={{fontSize: "12px", fontWeight: 500, color: coachT.t2}}>תוכניות נוכחיות וקודמות</Typography>}
            </Box>
          </Box>

          <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <Tooltip title="הגדרות תוכנית">
              <IconButton
                  onClick={() => setSettingsDialogOpen(true)}
                  sx={{
                    color: colors.textSecondary,
                    "&:hover": {
                      backgroundColor: colors.bgHover,
                      color: colors.textPrimary
                    }
                  }}
                  size="small"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="טקסט חימום">
              <IconButton
                  onClick={handleOpenWarmupDialog}
                  sx={{
                    color: colors.textSecondary,
                    "&:hover": {
                      backgroundColor: colors.bgHover,
                      color: colors.textPrimary
                    }
                  }}
                  size="small"
              >
                <WarmupIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={planOpen ? "סגירת הגדרות" : "פתיחת הגדרות"}>
              <IconButton
                  onClick={() => setPlanOpen(prv => !prv)}
                  sx={{
                    color: colors.textSecondary,
                    "&:hover": {
                      backgroundColor: colors.bgHover,
                      color: colors.textPrimary
                    }
                  }}
                  size="small"
              >
                {planOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Settings Panel */}
        <Collapse in={planOpen} timeout="auto">
          <Box sx={{
            padding: "16px 20px",
            backgroundColor: colors.bg,
            borderBottom: borders.default,
            display: "flex",
            justifyContent: "flex-end"
          }}>
            <Button
                onClick={() => setChangeProgramState(true)}
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: colors.dark,
                  color: colors.white,
                  fontSize: "13px",
                  padding: "6px 16px",
                  "&:hover": {
                    backgroundColor: "#334155"
                  }
                }}
                size="small"
            >
              שנה תוכנית
            </Button>
          </Box>
        </Collapse>

        {/* Coach: program cards (like תוכניות אימון, but programs) */}
        {coach ? (
            <Box sx={{display: "flex", flexDirection: "column", gap: 1.25, padding: "16px"}}>
              {historyPlans && historyPlans
                  .slice(0, showAll ? historyPlans.length : INITIAL_DISPLAY_COUNT)
                  .map((plan, i) => (
                      <CoachProgramCard
                          key={i}
                          plan={plan}
                          expanded={expandedRows.has(i)}
                          onToggle={() => toggleRowExpansion(i)}
                          onOpen={() => navigate(`/app/clients/${client.id}/${plan.id}/week/0/0`)}
                          renderDetailedInfo={renderDetailedInfo}
                      />
                  ))}
            </Box>
        ) : (
        /* Table */
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right" sx={th}>שם פרופיל</TableCell>
                <TableCell align="center" sx={th}>מידע בסיסי</TableCell>
                <TableCell align="center" sx={th}>מיקוד עיקרי</TableCell>
                <TableCell align="left" sx={th}>תאריך יצירה</TableCell>
                <TableCell sx={{...th, width: "50px", padding: "12px 8px"}}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyPlans && historyPlans
                  .slice(0, showAll ? historyPlans.length : INITIAL_DISPLAY_COUNT)
                  .map((plan, i) => {
                const isExpanded = expandedRows.has(i);
                return (
                    <Fragment key={i}>
                      <TableRow sx={{
                        backgroundColor: plan.active ? (coach ? coachT.greenLight : colors.successBg) : colors.white,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: plan.active ? (coach ? "#d2f5f0" : "#C6F6D5") : (coach ? coachT.bg : colors.bg)
                        }
                      }}>
                        <TableCell
                            align="right"
                            sx={td}
                            onClick={() => navigate(`/app/clients/${client.id}/${plan.id}/week/0/0`)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Typography sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: colors.textPrimary
                            }}>
                              {plan.nameHeb}
                            </Typography>
                            {plan.active && (
                                <StatusChip variant="success" label="פעיל" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={td}
                            onClick={() => navigate(`/app/clients/${client.id}/${plan.id}/week/0/0`)}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                            <Typography sx={{ fontSize: "12px", color: colors.textPrimary }}>
                              👤 {plan.gender === "FEMALE" ? "נקבה" : plan.gender === "MALE" ? "זכר" : "אין מידע"}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: colors.textSecondary }}>
                              🏋️ {plan.numOfWorkouts} אימונים/שבוע
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={td}
                            onClick={() => navigate(`/app/clients/${client.id}/${plan.id}/week/0/0`)}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                            <Typography sx={{ fontSize: "12px", color: colors.textPrimary }}>
                              💪 {bodyParts.find(tm => tm.value === plan.bodyPart)?.name || "אין מידע"}
                            </Typography>
                            {plan.secondaryMuscle && (
                                <Typography sx={{ fontSize: "12px", color: colors.textSecondary }}>
                                  ➕ {SecondaryMuscles.find(tm => tm.value === plan.secondaryMuscle)?.name}
                                </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                            align="left"
                            sx={td}
                            onClick={() => navigate(`/app/clients/${client.id}/${plan.id}/week/0/0`)}
                        >
                          <Typography sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: colors.textPrimary
                          }}>
                            {moment(moment.unix(plan.createdDate)).format("DD/MM/YY")}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{...td, padding: "12px 8px", width: "50px"}}>
                          <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRowExpansion(i);
                              }}
                              sx={{
                                color: colors.textSecondary,
                                "&:hover": {
                                  backgroundColor: colors.bgHover
                                }
                              }}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderDetailedInfo(plan)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Show More / Show Less Button */}
        {historyPlans && historyPlans.length > INITIAL_DISPLAY_COUNT && (
            <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: coach ? "0 16px 16px" : "12px 20px",
                  borderTop: coach ? "none" : borders.default
                }}
            >
              <Button
                  onClick={() => setShowAll(prev => !prev)}
                  variant="text"
                  endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    color: coach ? coachT.green : colors.textSecondary,
                    fontSize: "13px",
                    fontWeight: coach ? 700 : 500,
                    "&:hover": {
                      backgroundColor: coach ? "transparent" : colors.bgHover,
                      color: coach ? coachT.greenDark : colors.textPrimary
                    }
                  }}
              >
                {showAll
                    ? "הצג פחות"
                    : `הצג עוד ${historyPlans.length - INITIAL_DISPLAY_COUNT} תוכניות`
                }
              </Button>
            </Box>
        )}
      </Paper>
      </RTL>
  );
};

/* ── Coach program card (v2 — mirrors תוכניות אימון block cards) ── */
const Fact = ({children}) => (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "3px",
      fontSize: 11, fontWeight: 600, color: coachT.t2,
      background: coachT.bg, border: `1px solid ${coachT.border}`,
      borderRadius: "20px", padding: "2px 9px", whiteSpace: "nowrap",
    }}>
      {children}
    </Box>
);

const CoachProgramCard = ({plan, expanded, onToggle, onOpen, renderDetailedInfo}) => {
  const focus = bodyParts.find(b => b.value === plan.bodyPart)?.name;
  const level = experienceLevels.find(l => l.value === plan.experienceLevel)?.name;
  const gender = plan.gender === "FEMALE" ? "נקבה" : plan.gender === "MALE" ? "זכר" : null;
  return (
      <Box sx={{
        background: coachT.white,
        border: `1px solid ${plan.active ? "rgba(1,187,167,.3)" : coachT.border}`,
        borderRadius: coachT.rs,
        overflow: "hidden",
      }}>
        {/* Header row */}
        <Box sx={{display: "flex", alignItems: "center", gap: 1, padding: "11px 14px"}}>
          <Box onClick={onOpen} sx={{flex: 1, minWidth: 0, cursor: "pointer"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 0.5}}>
              <Typography sx={{fontSize: 13.5, fontWeight: 700, color: coachT.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                {plan.nameHeb}
              </Typography>
              {plan.active && (
                  <Box sx={{fontSize: 10, fontWeight: 700, color: coachT.greenText, background: coachT.greenLight, padding: "2px 8px", borderRadius: "20px", whiteSpace: "nowrap"}}>
                    ✓ פעיל
                  </Box>
              )}
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap"}}>
              <Fact><FitnessCenterIcon sx={{fontSize: 13}}/> {plan.numOfWorkouts} אימונים/שבוע</Fact>
              {focus && <Fact><BodyPartIcon sx={{fontSize: 13}}/> {focus}</Fact>}
              {level && <Fact>{level}</Fact>}
              {gender && <Fact>{gender}</Fact>}
              <Box component="span" sx={{fontSize: 11, color: coachT.t3, fontWeight: 600}}>
                {moment.unix(plan.createdDate).format("DD/MM/YY")}
              </Box>
            </Box>
          </Box>
          <IconButton size="small" onClick={onToggle}
              sx={{width: 28, height: 28, color: coachT.t2, flexShrink: 0, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
            {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
          </IconButton>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {renderDetailedInfo(plan)}
        </Collapse>
      </Box>
  );
};

const DetailRow = ({icon, label, value}) => (
    <Box sx={{minHeight: "24px"}}>
      <Box sx={{display: "flex", alignItems: "center", gap: 0.5, marginBottom: "2px"}}>
        <Box sx={{
          color: colors.textSecondary,
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          "& .MuiSvgIcon-root": {fontSize: "16px"}
        }}>
          {icon}
        </Box>
        <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary}}>
          {label}:
        </Typography>
      </Box>
      <Box>
        {typeof value === 'string' ? (
            <Typography sx={{fontSize: "14px", fontWeight: 600, color: colors.textPrimary, lineHeight: "18px"}}>
              {value}
            </Typography>
        ) : (
            value
        )}
      </Box>
    </Box>
);

export default ClientPlansSection;